import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { format } from 'date-fns'

import { Connection, SearchInput } from 'app/common/presentation'
import { PlusIcon } from 'app/common/presentation/icons'
import { getInitials } from 'app/common/presentation/initials'
import { useConnections } from 'app/connections/application'
import { InstalledConnection } from 'app/connections/types'

import { Avatar, Button, Chip, DropDown, PageBanner, Table } from 'components'

import styles from './Connections.module.scss'
import { mapToUrl } from './connectionUrlMapper'

const formatDate = (value: string) =>
  format(new Date(value), 'MMM do, hh:mm a').replace(/\b(AM|PM)\b/, (period) =>
    period.toLowerCase(),
  )

const Connections = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('connections')
  const { connections } = useConnections()

  const statusFilters = useMemo(
    () => [
      {
        label: t('overview.installed.filter.all'),
        value: 'all',
      },
      {
        label: t('overview.installed.filter.active'),
        value: 'active',
      },
      {
        label: t('overview.installed.filter.inactive'),
        value: 'disabled',
      },
      {
        label: t('overview.installed.filter.pending'),
        value: 'draft',
      },
    ],
    [t],
  )

  const [statusFilter, setStatusFilter] = useState(statusFilters[0].value)
  const [search, setSearch] = useState('')

  const categoryFilters = useMemo(
    () => [
      { label: t('overview.installed.filter.allCategories'), value: 'all' },
      ...Array.from(new Set(connections.map(({ category }) => category))).map((category) => ({
        label: t(`category.${category}`),
        value: category,
      })),
    ],
    [connections, t],
  )
  const [categoryFilter, setCategoryFilter] = useState('all')

  const filteredConnections = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    return connections.filter(
      (connection) =>
        (statusFilter === 'all' || connection.status === statusFilter) &&
        (categoryFilter === 'all' || connection.category === categoryFilter) &&
        (!query ||
          t(`available.${connection.name}.displayName`).toLocaleLowerCase().includes(query) ||
          t(`category.${connection.category}`).toLocaleLowerCase().includes(query)),
    )
  }, [categoryFilter, connections, search, statusFilter, t])

  const getAccounts = (connection: InstalledConnection) => {
    const config = connection.config as Record<string, unknown>
    const collection = config.accounts ?? config.advertisers ?? config.shops
    if (!Array.isArray(collection)) return '-'

    const values = collection.map((entry) => {
      if (typeof entry === 'string') return entry
      if (!entry || typeof entry !== 'object') return String(entry)
      const item = entry as Record<string, unknown>
      const label = item.name ?? item.displayName ?? item.shop ?? item.id
      return typeof label === 'string' || typeof label === 'number' ? String(label) : '-'
    })
    return values.length ? values.join(' · ') : '-'
  }

  return (
    <>
      <PageBanner subtitle={t('overview.description')} title={t('overview.headline')}>
        <Button onClick={() => void navigate('/connections/browse')} rightIcon={<PlusIcon />}>
          {t('overview.newConnection')}
        </Button>
      </PageBanner>

      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          onChange={setSearch}
          placeholder={t('overview.installed.search')}
          value={search}
        />
        <div className={styles.filters}>
          <DropDown
            className={styles.filter}
            data={categoryFilters}
            onSelect={setCategoryFilter}
            selected={categoryFilter}
          />
          <DropDown
            className={styles.filter}
            data={statusFilters}
            onSelect={setStatusFilter}
            selected={statusFilter}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <Table.Table
          className={styles.table}
          columns={[
            t('overview.installed.table.platform'),
            t('overview.installed.table.category'),
            t('overview.installed.table.accounts'),
            t('overview.installed.table.signals'),
            t('overview.installed.table.createdAt'),
            t('overview.installed.table.createdBy'),
            t('overview.installed.table.status'),
          ]}
          data={filteredConnections}
          renderRow={(connection) => (
            <Table.Row
              key={connection.id}
              onClick={() => navigate(`/connections/${mapToUrl(connection.name)}/manage`)}
            >
              <Table.Cell className={styles.platformCell}>
                <Connection connection={connection} />
              </Table.Cell>
              <Table.Cell>{t(`category.${connection.category}`)}</Table.Cell>
              <Table.Cell>{getAccounts(connection)}</Table.Cell>
              <Table.Cell>{t('overview.emptyValue')}</Table.Cell>
              <Table.Cell className={styles.dateCell}>
                {connection.createdAt ? formatDate(connection.createdAt) : '-'}
              </Table.Cell>
              <Table.Cell className={styles.creatorCell}>
                {connection.createdBy ? (
                  <>
                    <Avatar
                      className={styles.creatorAvatar}
                      initials={getInitials(connection.createdBy)}
                    />
                    <span>
                      {connection.createdBy.firstName} {connection.createdBy.lastName}
                    </span>
                  </>
                ) : (
                  t('overview.emptyValue')
                )}
              </Table.Cell>
              <Table.Cell>
                <Chip
                  color={
                    connection.error ? 'red' : connection.status === 'active' ? 'green' : 'neutral'
                  }
                >
                  {t(`status.${connection.status}`)}
                </Chip>
              </Table.Cell>
            </Table.Row>
          )}
        />
      </div>
    </>
  )
}

export default Connections
